import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Briefcase, ChevronRight, MapPin, Clock, ArrowLeft, Upload, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import SEO, { generateBreadcrumbSchema, generateJobPostingSchema } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Styled HTML content component for rich text rendering
const RichContent = ({ html, className = '' }) => {
  if (!html) return null;
  return (
    <>
      <div 
        className={`rich-content ${className}`}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
      />
      <style>{`
        .rich-content h1 { font-size: 1.75em; font-weight: 700; margin: 0.75em 0 0.5em; color: #1c1917; }
        .rich-content h2 { font-size: 1.4em; font-weight: 600; margin: 0.75em 0 0.5em; color: #1c1917; }
        .rich-content h3 { font-size: 1.2em; font-weight: 600; margin: 0.5em 0 0.25em; color: #1c1917; }
        .rich-content h4 { font-size: 1.1em; font-weight: 600; margin: 0.5em 0 0.25em; color: #1c1917; }
        .rich-content p { margin: 0.5em 0; line-height: 1.7; }
        .rich-content ul, .rich-content ol { margin: 0.75em 0; padding-left: 1.5em; }
        .rich-content ul { list-style-type: disc; }
        .rich-content ol { list-style-type: decimal; }
        .rich-content li { margin: 0.25em 0; line-height: 1.6; }
        .rich-content blockquote { border-left: 3px solid #d6d3d1; padding-left: 1em; margin: 1em 0; color: #57534e; font-style: italic; }
        .rich-content a { color: #321a27; text-decoration: underline; }
        .rich-content a:hover { color: #1d4ed8; }
        .rich-content strong, .rich-content b { font-weight: 600; }
        .rich-content em, .rich-content i { font-style: italic; }
        .rich-content hr { border: none; border-top: 1px solid #e7e5e4; margin: 1.5em 0; }
        .rich-content u { text-decoration: underline; }
        .rich-content s, .rich-content strike { text-decoration: line-through; }
      `}</style>
    </>
  );
};

const JobDetailPage = () => {
  const { t, language } = useLanguage();
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplication, setShowApplication] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [cvFile, setCvFile] = useState(null);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const res = await axios.get(`${API}/jobs/${id}`);
        setJob(res.data);
      } catch (error) {
        console.error('Error fetching job:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 10 * 1024 * 1024) {
      setCvFile(file);
    } else if (file) {
      toast.error(language === 'sv' ? 'Filen är för stor (max 10MB)' : 'File is too large (max 10MB)');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append('job_id', job.id);  // Use job.id instead of slug
      submitData.append('first_name', formData.firstName);
      submitData.append('last_name', formData.lastName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('message', formData.message);
      if (cvFile) submitData.append('cv', cvFile);
      await axios.post(`${API}/applications`, submitData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmitted(true);
      toast.success(t('application.success'));
    } catch (error) {
      toast.error(t('application.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#FAFAF8] min-h-screen pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-stone-200 rounded w-2/3" />
            <div className="h-4 bg-stone-200 rounded w-1/3" />
            <div className="h-32 bg-stone-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="bg-[#FAFAF8] min-h-screen pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <Briefcase className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <p className="text-stone-600">{language === 'sv' ? 'Jobbet hittades inte' : 'Job not found'}</p>
          <Link to="/jobb">
            <Button variant="outline" className="mt-4 border-stone-300 text-stone-700 rounded-none">
              <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const title = language === 'sv' ? job.title_sv : job.title_en;
  const summary = language === 'sv' ? job.summary_sv : job.summary_en;
  const description = language === 'sv' ? job.description_sv : job.description_en;
  const requirements = language === 'sv' ? job.requirements_sv : job.requirements_en;
  const qualifications = language === 'sv' ? job.qualifications_sv : job.qualifications_en;

  const jobSlug = job.slug || job.id;
  const jobIdDisplay = job.job_id || '';

  return (
    <div data-testid="job-detail-page" className="bg-[#FAFAF8] min-h-screen pt-24">
      <SEO 
        title={jobIdDisplay ? `${title} (${jobIdDisplay}) | ${job.location}` : `${title} | ${job.location}`}
        description={language === 'sv'
          ? `Ledigt jobb: ${title}${jobIdDisplay ? ` (${jobIdDisplay})` : ''} i ${job.location}. ${t(`roleTypes.${job.role_type}`)} hos Flowingo AB. Ansök nu!`
          : `Job opening: ${title}${jobIdDisplay ? ` (${jobIdDisplay})` : ''} in ${job.location}. ${t(`roleTypes.${job.role_type}`)} at Flowingo AB. Apply now!`}
        language={language}
        canonical={`https://flowingo.se/jobb/${jobSlug}`}
        schema={[
          generateBreadcrumbSchema([
            { name: language === 'sv' ? 'Hem' : 'Home', url: 'https://flowingo.se' },
            { name: language === 'sv' ? 'Karriär' : 'Careers', url: 'https://flowingo.se/jobb' },
            { name: title, url: `https://flowingo.se/jobb/${jobSlug}` },
          ]),
          generateJobPostingSchema(job, language),
        ]}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <nav className="flex items-center gap-2 text-sm text-stone-500 mb-8" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-stone-900">{t('nav.home')}</Link>
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
          <Link to="/jobb" className="hover:text-stone-900">{t('nav.jobs')}</Link>
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
          <span className="text-stone-900 truncate max-w-[200px]" aria-current="page">{title}</span>
        </nav>

        <article itemScope itemType="https://schema.org/JobPosting">
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-3 py-1 text-sm bg-stone-100 text-stone-700">{t(`roleTypes.${job.role_type}`)}</span>
              <span className="px-3 py-1 text-sm bg-stone-100 text-stone-700">{t(`experienceLevels.${job.experience_level}`)}</span>
              <span className="px-3 py-1 text-sm bg-stone-100 text-stone-700">{job.category}</span>
            </div>
            <h1 data-testid="job-title" className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4" itemProp="title">{title}</h1>
            <div className="flex items-center gap-6 text-stone-600 flex-wrap">
              <span className="flex items-center gap-2" itemProp="jobLocation" itemScope itemType="https://schema.org/Place">
                <MapPin className="w-5 h-5" aria-hidden="true" /> 
                <span itemProp="address">{job.location}</span>
              </span>
              {job.deadline && (
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5" aria-hidden="true" /> {t('jobs.deadline')}: 
                  <time itemProp="validThrough" dateTime={job.deadline}>
                    {new Date(job.deadline).toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US')}
                  </time>
                </span>
              )}
              {job.job_id && (
                <span className="font-mono text-sm bg-stone-100 px-3 py-1 border border-stone-200">
                  {language === 'sv' ? 'Jobb-ID:' : 'Job-ID:'} {job.job_id}
                </span>
              )}
            </div>
          </div>

        {summary && (
          <div className="bg-white p-6 border border-stone-200 mb-8">
            <RichContent html={summary} className="text-stone-700 text-lg" />
          </div>
        )}

        {description && (
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">{t('jobs.description')}</h2>
            <RichContent html={description} className="text-stone-600" />
          </section>
        )}

        {requirements?.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">{t('jobs.requirements')}</h2>
            <ul className="space-y-2">
              {requirements.map((req, idx) => (
                <li key={idx} className="flex items-start gap-3 text-stone-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-stone-400 mt-2 shrink-0" />{req}
                </li>
              ))}
            </ul>
          </section>
        )}

        {qualifications?.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">{t('jobs.qualifications')}</h2>
            <ul className="space-y-2">
              {qualifications.map((qual, idx) => (
                <li key={idx} className="flex items-start gap-3 text-stone-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-stone-300 mt-2 shrink-0" />{qual}
                </li>
              ))}
            </ul>
          </section>
        )}
        </article>

        {!showApplication && !submitted && (
          <div className="flex gap-4 mb-12">
            <Button size="lg" onClick={() => setShowApplication(true)} data-testid="apply-button" className="bg-stone-900 text-white hover:bg-stone-800 rounded-none">
              {t('jobs.apply')}
            </Button>
            <Link to="/jobb">
              <Button variant="outline" size="lg" className="border-stone-300 text-stone-700 hover:bg-stone-100 rounded-none">
                <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back')}
              </Button>
            </Link>
          </div>
        )}

        {showApplication && !submitted && (
          <div className="bg-white p-8 border border-stone-200" data-testid="application-form">
            <h2 className="text-2xl font-semibold text-stone-900 mb-6">{t('application.title')}</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="firstName" className="text-stone-700 mb-2 block">{t('application.firstName')} *</Label>
                  <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} required className="bg-white border-stone-300 text-stone-900 rounded-none" data-testid="input-first-name" />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-stone-700 mb-2 block">{t('application.lastName')} *</Label>
                  <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} required className="bg-white border-stone-300 text-stone-900 rounded-none" data-testid="input-last-name" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email" className="text-stone-700 mb-2 block">{t('application.email')} *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required className="bg-white border-stone-300 text-stone-900 rounded-none" data-testid="input-email" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-stone-700 mb-2 block">{t('application.phone')} *</Label>
                  <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} required className="bg-white border-stone-300 text-stone-900 rounded-none" data-testid="input-phone" />
                </div>
              </div>
              <div>
                <Label htmlFor="message" className="text-stone-700 mb-2 block">{t('application.message')}</Label>
                <Textarea id="message" name="message" value={formData.message} onChange={handleInputChange} rows={4} className="bg-white border-stone-300 text-stone-900 resize-none rounded-none" data-testid="input-message" />
              </div>
              <div>
                <Label className="text-stone-700 mb-2 block">{t('application.uploadCv')}</Label>
                <div className="border-2 border-dashed border-stone-300 p-6 text-center hover:border-stone-400 transition-colors">
                  <input type="file" id="cv" accept=".pdf,.doc,.docx" onChange={handleFileChange} className="hidden" data-testid="input-cv" />
                  <label htmlFor="cv" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                    {cvFile ? <p className="text-stone-900">{cvFile.name}</p> : <p className="text-stone-500">{language === 'sv' ? 'Klicka för att ladda upp CV (PDF, DOC)' : 'Click to upload CV (PDF, DOC)'}</p>}
                  </label>
                </div>
              </div>
              <div className="flex gap-4">
                <Button type="submit" disabled={submitting} data-testid="submit-application" className="bg-stone-900 text-white hover:bg-stone-800 rounded-none">
                  {submitting ? '...' : t('application.submit')}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowApplication(false)} className="border-stone-300 text-stone-700 hover:bg-stone-100 rounded-none">
                  {language === 'sv' ? 'Avbryt' : 'Cancel'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {submitted && (
          <div className="bg-emerald-50 border border-emerald-200 p-8 text-center" data-testid="application-success">
            <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-stone-900 mb-2">{t('application.success')}</h2>
            <p className="text-stone-600 mb-6">{language === 'sv' ? 'Vi har tagit emot din ansökan och kommer att höra av oss inom kort.' : 'We have received your application and will be in touch soon.'}</p>
            <Link to="/jobb">
              <Button variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-100 rounded-none">
                <ArrowLeft className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Tillbaka till karriär' : 'Back to careers'}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetailPage;
